/**
 * RecentProofs — the user's signed proofs, rendered as a gallery of cards.
 *
 * Source of truth: the server (`useProofs()` → `/api/v1/proofs`).
 * The local `proofsStore` is the offline cache; `useProofs` falls back
 * to it via `placeholderData` when the server is unreachable, so the
 * gallery stays populated on a flaky network.
 *
 * Render policy:
 *   - Sort newest first by signed timestamp.
 *   - Show the user's full proof set, not a slice — the spec calls
 *     the Vault a "jewelry box," and a vault that hides 90% of its
 *     contents fails that promise.
 *   - 3-up grid on `lg`+, 2-up on `md`, 1-up below — per spec §5.3.
 *   - Each card uses the motion library's `lift` on hover and arrives
 *     in the cascade via `arrive` + parent `stagger` (§5.9).
 *   - Empty state is a warm invitation, not a void: copy + a
 *     ghosted card silhouette (§5.7).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  Download,
  FileText,
  Lightbulb,
  FileSignature,
  Receipt,
  Mail,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { buildProofCertificate, type Proof, type ProofKind } from '@/stores/proofsStore';
import { useProofs } from '@/hooks/useProofs';
import { downloadJson, shortHash } from '@/utils/hashing';
import { arrive, lift, stagger } from '@/lib/motion';

const ICON_BY_KIND: Record<ProofKind, LucideIcon> = {
  file: FileText,
  idea: Lightbulb,
  invoice: Receipt,
  contract: FileSignature,
  memo: Mail,
  document: FileText,
};

export function RecentProofs() {
  const { t, i18n } = useTranslation('dashboard');
  const { data: proofs = [] } = useProofs();

  // Defensive sort — server presumably returns newest first, but the
  // gallery's promise is "my latest proof, top-left" and we don't
  // want a stale cache or an out-of-order server to break that.
  const sorted = useMemo(
    () =>
      [...proofs].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      ),
    [proofs],
  );

  if (sorted.length === 0) {
    return <EmptyVault />;
  }

  return (
    <section aria-labelledby="recent-heading" className="mt-6">
      <header className="mb-4 flex items-baseline justify-between gap-4">
        <h2
          id="recent-heading"
          className="text-base font-semibold tracking-tight text-ink"
        >
          {t('recent.title')}
        </h2>
        <span className="text-xs text-ink-muted">
          {sorted.length} {sorted.length === 1 ? 'proof' : 'proofs'}
        </span>
      </header>

      <motion.ul
        // The list itself owns the stagger; each card owns its own
        // arrive. This pattern lets us reorder or paginate the cards
        // without re-tuning the per-card animation.
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
      >
        {sorted.map((proof) => (
          <ProofCard key={proof.id} proof={proof} locale={i18n.language} />
        ))}
      </motion.ul>
    </section>
  );
}

// ── Card ─────────────────────────────────────────────────────────

function ProofCard({ proof, locale }: { proof: Proof; locale: string }) {
  const { t } = useTranslation('dashboard');
  const Icon = ICON_BY_KIND[proof.kind] ?? FileText;
  const date = new Date(proof.timestamp);
  const relative = formatRelative(date, locale);
  const absolute = formatAbsolute(date, locale);

  const handleDownload = () => {
    const cert = buildProofCertificate(proof);
    downloadJson(`${slug(proof.name)}.proof.json`, cert);
  };

  return (
    <motion.li
      variants={arrive}
      whileHover={lift}
      className="group flex flex-col gap-3 rounded-2xl border border-border bg-surface p-5 shadow-subtle"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-spark-soft text-spark"
            aria-hidden
          >
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink" title={proof.name}>
              {proof.name}
            </p>
            <p className="mt-0.5 text-xs text-ink-muted">
              <KindBadge kind={proof.kind} />
              <span className="mx-1.5 text-ink-subtle" aria-hidden>·</span>
              <time dateTime={proof.timestamp} title={absolute}>
                {relative}
              </time>
            </p>
          </div>
        </div>
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success"
          aria-label={t('recent.signed_label')}
        >
          <CheckCircle2 className="h-3 w-3" aria-hidden />
          {t('recent.signed_label')}
        </span>
      </header>

      <code
        className="block truncate rounded-md bg-surface-raised px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-ink-subtle"
        title={proof.hash}
      >
        {shortHash(proof.hash, 8, 6)}
      </code>

      <footer className="mt-auto flex items-center justify-end pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDownload}
          leading={<Download className="h-4 w-4" aria-hidden />}
          aria-label={t('recent.download')}
        >
          {t('recent.download')}
        </Button>
      </footer>
    </motion.li>
  );
}

function KindBadge({ kind }: { kind: ProofKind }) {
  const { t } = useTranslation('dashboard');
  return (
    <span className="font-medium text-ink-muted">
      {t(`recent.kind.${kind}`)}
    </span>
  );
}

// ── Empty state ──────────────────────────────────────────────────

function EmptyVault() {
  const { t } = useTranslation('dashboard');
  return (
    <motion.section
      variants={arrive}
      initial="hidden"
      animate="visible"
      aria-labelledby="recent-empty-heading"
      className="mt-6"
    >
      <div className="relative overflow-hidden rounded-2xl border border-dashed border-border bg-surface/40 p-10 text-center md:p-14">
        {/* Soft indigo bloom — same animate-pulse-style breath as the
            marketing site's hero, calmer here. Pure decoration. */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-spark/10 blur-3xl"
        />

        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-spark-soft text-spark">
          <Sparkles className="h-6 w-6" aria-hidden />
        </div>

        <h2
          id="recent-empty-heading"
          className="mt-5 text-lg font-semibold text-ink"
        >
          {t('recent.empty_title')}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted">
          {t('recent.empty_body')}
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/spark" className="contents">
            <Button variant="primary">{t('recent.empty_primary_cta')}</Button>
          </Link>
          <Link to="/verify" className="contents">
            <Button variant="ghost">{t('recent.empty_secondary_cta')}</Button>
          </Link>
        </div>

        {/* Ghosted proof-card silhouette — outline only, no fake
            data — to give the user a preview of the shape their
            first proof will take. Per spec §5.7. */}
        <div
          aria-hidden
          className="mx-auto mt-8 grid max-w-xl grid-cols-1 gap-3 opacity-50 sm:grid-cols-2"
        >
          <div className="h-24 rounded-2xl border border-dashed border-border" />
          <div className="hidden h-24 rounded-2xl border border-dashed border-border sm:block" />
        </div>
      </div>
    </motion.section>
  );
}

// ── Utilities ────────────────────────────────────────────────────

function slug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'proof'
  );
}

function formatRelative(date: Date, locale: string): string {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffMin < 1) return rtf.format(0, 'minute');
  if (diffMin < 60) return rtf.format(-diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return rtf.format(-diffHr, 'hour');
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return rtf.format(-diffDay, 'day');
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
}

function formatAbsolute(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}
